import { useCallback, useEffect, useState } from 'react';

// Client-side starred-message store. Persistence lives in localStorage keyed by
// userId so each team member on a shared device keeps their own stars. Scope is
// per-user, not per-room: a message starred in team-chat stays starred when the
// user opens the starred panel from a DM.
//
// Why localStorage (not the server): the `chat_messages` DynamoDB row has no
// star column and the chat routes don't expose a star mutation. Adding both was
// out of scope for this feature cut — if starred messages need to survive
// browser clears or follow the user across devices, promote this to a real
// backend field + GSI (`user_starred_messages`) and replace the localStorage
// read/write with a REST call.

const STORAGE_PREFIX = 'chat:starred:';
const CHANGE_EVENT = 'chat:starred-changed';

function storageKey(userId: string) {
  return `${STORAGE_PREFIX}${userId}`;
}

function readStarredIds(userId: string): string[] {
  if (!userId) return [];
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function writeStarredIds(userId: string, ids: string[]) {
  if (!userId) return;
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(ids));
    // Notify any other mounted instance of the hook in the same tab — the
    // native `storage` event only fires in *other* tabs, so we emit our own.
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { userId } }));
  } catch {
    // quota exceeded / private-mode — silently ignore, next toggle will retry
  }
}

export interface UseStarredMessagesResult {
  starredIds: Set<string>;
  isStarred: (messageId: string) => boolean;
  toggleStar: (messageId: string) => void;
  clearStars: () => void;
}

export function useStarredMessages(userId: string | null | undefined): UseStarredMessagesResult {
  const [ids, setIds] = useState<string[]>(() => (userId ? readStarredIds(userId) : []));

  useEffect(() => {
    if (!userId) {
      setIds([]);
      return;
    }
    setIds(readStarredIds(userId));

    const onChange = (e: Event) => {
      const detail = (e as CustomEvent).detail as { userId?: string } | undefined;
      if (!detail || detail.userId === userId) {
        setIds(readStarredIds(userId));
      }
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === storageKey(userId)) setIds(readStarredIds(userId));
    };
    window.addEventListener(CHANGE_EVENT, onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, [userId]);

  const starredIds = new Set(ids);

  const isStarred = useCallback(
    (messageId: string) => starredIds.has(messageId),
    // starredIds is derived from ids — depend on ids so the callback updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ids],
  );

  const toggleStar = useCallback(
    (messageId: string) => {
      if (!userId || !messageId) return;
      const current = readStarredIds(userId);
      const next = current.includes(messageId)
        ? current.filter((id) => id !== messageId)
        : [...current, messageId];
      writeStarredIds(userId, next);
      setIds(next);
    },
    [userId],
  );

  const clearStars = useCallback(() => {
    if (!userId) return;
    writeStarredIds(userId, []);
    setIds([]);
  }, [userId]);

  return { starredIds, isStarred, toggleStar, clearStars };
}
